import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Box, Typography, Paper, Button, TextField, MenuItem, Chip, Stack, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, IconButton, Tooltip, CircularProgress } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SendIcon from '@mui/icons-material/Send';
import RuleIcon from '@mui/icons-material/Rule';

const TOKEN_KEY = 'control_comprobantes_token';

const ETIQUETA_MOTIVO = {
  ERROR_VALIDACION: 'Error de validación',
  ERROR_ENVIO: 'Error de envío',
  ERROR_REINTENTO: 'Reintento pendiente',
  RECHAZADO: 'Rechazado por SUNAT',
  EN_COLA_MAS_30_MIN: 'En cola más de 30 min',
  RECHAZADA: 'Anulación rechazada',
  ERROR_DEFINITIVO: 'Anulación con error definitivo',
  ERROR_ENVIO_RESUMEN: 'Resumen con error de envío',
  ERROR_CONSULTA: 'Anulación con error de consulta',
};

const ETIQUETA_ESTADO = {
  ACTIVA: 'Activa',
  EN_REVISION: 'En revisión',
  RESUELTA_MANUAL: 'Resuelta',
  RESUELTA_AUTO: 'Resuelta automáticamente',
  FUERA_DE_ALCANCE: 'Fuera de alcance (antes del 01/10/2026)',
};

// Para una fecha-calendario pura (sin hora, ej. fecha de emisión del documento) se arma
// DD/MM/AAAA leyendo los dígitos del string directo, sin pasar por Date/toLocaleDateString:
// esos convierten a la zona horaria del navegador, y una fecha "YYYY-MM-DDT00:00:00.000Z"
// puede mostrar el día anterior para usuarios en huso horario negativo (ej. Perú, UTC-5).
const fmtFechaEmision = (valor) => {
  if (!valor) return '';
  const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

const obtenerToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
};
const guardarToken = (t) => {
  try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* el navegador puede bloquear el almacenamiento */ }
};
const borrarToken = () => {
  try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* sin efecto */ }
};

const ControlComprobantes = () => {
  const [token, setToken] = useState(obtenerToken());
  const [password, setPassword] = useState('');
  const [errorLogin, setErrorLogin] = useState('');
  const [entrando, setEntrando] = useState(false);
  const [alertas, setAlertas] = useState([]);
  const [resumen, setResumen] = useState({ activas: 0, resueltas: 0, detalle: [] });
  const [error, setError] = useState('');
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroMotivo, setFiltroMotivo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [cargando, setCargando] = useState(false);
  const [accion, setAccion] = useState(null);
  const [notaAccion, setNotaAccion] = useState('');
  const [historial, setHistorial] = useState(null);
  const [datosSunat, setDatosSunat] = useState(null); // { alerta, datos } | { alerta, cargando: true } | { alerta, error }
  const [verificando, setVerificando] = useState(null); // id de la alerta en verificación
  const [resultadoVerificacion, setResultadoVerificacion] = useState(null); // { alertaId, ok, mensaje }
  const [reenviando, setReenviando] = useState(null); // id de la alerta en reenvío/sondeo
  const [estadoEnVivo, setEstadoEnVivo] = useState(null); // { alertaId, estadoCpe, estadoJob, mensaje, terminado }
  const [accionEstadoReal, setAccionEstadoReal] = useState(null); // { alerta }
  const [estadoRealElegido, setEstadoRealElegido] = useState('ENVIADO');
  const [notaEstadoReal, setNotaEstadoReal] = useState('');

  const cabeceras = useCallback(() => ({ 'x-control-token': token }), [token]);

  const cerrarSesionControl = useCallback(() => {
    borrarToken();
    setToken('');
    setAlertas([]);
  }, []);

  const cargar = useCallback(async () => {
    if (!token) return;
    setCargando(true);
    setError('');
    try {
      const params = {};
      if (filtroEmpresa) params.empresa = filtroEmpresa;
      if (filtroMotivo) params.motivo = filtroMotivo;
      if (filtroEstado) params.estado = filtroEstado;
      const [lista, res] = await Promise.all([
        axios.get('/api/control/alertas', { params, headers: cabeceras() }),
        axios.get('/api/control/resumen', { headers: cabeceras() }),
      ]);
      setAlertas(lista.data || []);
      setResumen(res.data || { activas: 0, resueltas: 0, detalle: [] });
    } catch (err) {
      if (err.response?.status === 401) {
        cerrarSesionControl();
        setErrorLogin('La sesión de control venció. Ingresa de nuevo.');
      } else {
        setError(err.response?.data?.error || 'No se pudieron cargar las alertas.');
      }
    } finally {
      setCargando(false);
    }
  }, [token, cabeceras, filtroEmpresa, filtroMotivo, filtroEstado, cerrarSesionControl]);

  useEffect(() => { cargar(); }, [cargar]);

  const ingresar = async (e) => {
    e.preventDefault();
    setEntrando(true);
    setErrorLogin('');
    try {
      const res = await axios.post('/api/control/login', { password });
      guardarToken(res.data.token);
      setToken(res.data.token);
      setPassword('');
    } catch (err) {
      setErrorLogin(err.response?.data?.error || 'No se pudo validar la contraseña maestra.');
    } finally {
      setEntrando(false);
    }
  };

  const confirmarAccion = async () => {
    if (!accion) return;
    if (notaAccion.trim().length < 5) return;
    try {
      await axios.post(`/api/control/alertas/${accion.alerta.id}/estado`, { estado: accion.estado, nota: notaAccion.trim() }, { headers: cabeceras() });
      setAccion(null);
      setNotaAccion('');
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el cambio.');
    }
  };

  const verHistorial = async (alerta) => {
    try {
      const res = await axios.get(`/api/control/alertas/${alerta.id}/historial`, { headers: cabeceras() });
      setHistorial({ alerta, filas: res.data || [] });
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el historial.');
    }
  };

  const verDatosSunat = async (alerta) => {
    setDatosSunat({ alerta, cargando: true });
    try {
      const res = await axios.get(`/api/control/alertas/${alerta.id}/datos-sunat`, { headers: cabeceras() });
      setDatosSunat({ alerta, datos: res.data });
    } catch (err) {
      setDatosSunat({ alerta, error: err.response?.data?.error || 'No se pudieron traer los datos del documento.' });
    }
  };

  const copiar = async (texto) => {
    try { await navigator.clipboard.writeText(String(texto ?? '')); } catch { /* el navegador puede bloquear el portapapeles */ }
  };

  const copiarTodo = (datos) => {
    const lineas = [
      `RUC emisor: ${datos.ruc}`,
      `Tipo de comprobante: ${datos.tipoComprobanteLabel}`,
      `Serie: ${datos.serie}`,
      `Número: ${datos.numero}`,
      `Tipo/N° doc. receptor: ${datos.numDocReceptor ? `${datos.tipoDocReceptorLabel} ${datos.numDocReceptor}` : 'Sin documento'}`,
      `Fecha de emisión: ${datos.fechaEmision}`,
      ...(datos.importeTotal != null ? [`Importe total: ${datos.importeTotal}`] : []),
    ];
    copiar(lineas.join('\n'));
  };

  const verificarSunat = async (alerta) => {
    setVerificando(alerta.id);
    setResultadoVerificacion(null);
    try {
      const res = await axios.post(`/api/control/alertas/${alerta.id}/verificar-sunat`, {}, { headers: cabeceras() });
      setResultadoVerificacion({ alertaId: alerta.id, ...res.data });
      if (res.data?.resuelto) cargar();
    } catch (err) {
      setResultadoVerificacion({ alertaId: alerta.id, ok: false, mensaje: err.response?.data?.error || 'No se pudo verificar en SUNAT.' });
    } finally {
      setVerificando(null);
    }
  };

  const ESTADOS_JOB_ACTIVOS = ['PENDIENTE', 'PROCESANDO', 'REINTENTAR'];

  const reenviar = async (alerta) => {
    setReenviando(alerta.id);
    setEstadoEnVivo({ alertaId: alerta.id, mensaje: 'Encolando...', terminado: false });
    try {
      const res = await axios.post(`/api/control/alertas/${alerta.id}/reenviar`, {}, { headers: cabeceras() });
      if (!res.data?.ok) {
        setEstadoEnVivo({ alertaId: alerta.id, mensaje: res.data?.motivo || 'No se pudo encolar.', terminado: true });
        return;
      }
      setEstadoEnVivo({ alertaId: alerta.id, mensaje: res.data.mensaje, terminado: false });
      // Consulta el estado real cada 3s hasta ~60s, o hasta que el job salga de los estados activos.
      for (let intento = 0; intento < 20; intento++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const est = await axios.get(`/api/control/alertas/${alerta.id}/estado-actual`, { headers: cabeceras() });
          const d = est.data;
          const terminado = !d.estadoJob || !ESTADOS_JOB_ACTIVOS.includes(d.estadoJob);
          setEstadoEnVivo({ alertaId: alerta.id, estadoCpe: d.estadoCpe, estadoJob: d.estadoJob, mensaje: d.mensaje, terminado });
          if (terminado) {
            if (['ENVIADO', 'ACEPTADO'].includes(d.estadoCpe)) cargar();
            break;
          }
        } catch { /* reintenta en el próximo ciclo */ }
      }
    } catch (err) {
      setEstadoEnVivo({ alertaId: alerta.id, mensaje: err.response?.data?.error || 'No se pudo reenviar.', terminado: true });
    } finally {
      setReenviando(null);
    }
  };

  const actualizarEstadoReal = async () => {
    if (!accionEstadoReal) return;
    if (notaEstadoReal.trim().length < 5) return;
    try {
      await axios.post(
        `/api/control/alertas/${accionEstadoReal.alerta.id}/actualizar-estado-real`,
        { estadoReal: estadoRealElegido, nota: notaEstadoReal.trim() },
        { headers: cabeceras() }
      );
      setAccionEstadoReal(null);
      setNotaEstadoReal('');
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo actualizar el estado real.');
    }
  };

  if (!token) {
    return (
      <Box sx={{ maxWidth: 420, mx: 'auto', mt: 4, px: 2 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Control de comprobantes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ingresa la contraseña maestra para ver los comprobantes con problemas de todas las empresas.
          </Typography>
          <form onSubmit={ingresar}>
            <Stack spacing={2}>
              <TextField label="Contraseña maestra" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" fullWidth required />
              {errorLogin && <Alert severity="error">{errorLogin}</Alert>}
              <Button type="submit" variant="contained" disabled={entrando || !password}>
                {entrando ? 'Validando...' : 'Entrar'}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Box>
    );
  }

  const empresas = [...new Set(resumen.detalle.map(d => d.empresa))].sort();
  const motivos = [...new Set(resumen.detalle.map(d => d.motivo))].sort();

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Control de comprobantes</Typography>
          <Typography variant="body2" color="text.secondary">Comprobantes con problemas en todas las empresas.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip color="error" label={`Activas: ${resumen.activas}`} />
          <Chip color="success" variant="outlined" label={`Resueltas: ${resumen.resueltas}`} />
          <Button size="small" onClick={cerrarSesionControl}>Salir del control</Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
        <TextField select size="small" label="Empresa" value={filtroEmpresa} onChange={(e) => setFiltroEmpresa(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">Todas</MenuItem>
          {empresas.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Motivo" value={filtroMotivo} onChange={(e) => setFiltroMotivo(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="">Todos</MenuItem>
          {motivos.map(m => <MenuItem key={m} value={m}>{ETIQUETA_MOTIVO[m] || m}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Estado" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">Activas y en revisión</MenuItem>
          <MenuItem value="TODAS">Todas</MenuItem>
          <MenuItem value="ACTIVA">Activa</MenuItem>
          <MenuItem value="EN_REVISION">En revisión</MenuItem>
          <MenuItem value="RESUELTA_MANUAL">Resuelta</MenuItem>
          <MenuItem value="RESUELTA_AUTO">Resuelta automáticamente</MenuItem>
          <MenuItem value="FUERA_DE_ALCANCE">Fuera de alcance</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={cargar} disabled={cargando}>{cargando ? 'Actualizando...' : 'Actualizar'}</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Empresa</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell>Detalle SUNAT / sistema</TableCell>
              <TableCell>Fecha emisión</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alertas.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">Sin alertas con los filtros actuales.</TableCell></TableRow>
            )}
            {alertas.map(a => (
              <TableRow key={a.id} hover>
                <TableCell>{a.empresa}</TableCell>
                <TableCell>{a.origen === 'ANULACION' ? 'Anulación' : a.origen === 'GUIA' ? 'Guía' : 'Comprobante'}</TableCell>
                <TableCell>{a.tipo_doc} {a.serie}-{a.numero}</TableCell>
                <TableCell>{ETIQUETA_MOTIVO[a.motivo] || a.motivo}</TableCell>
                <TableCell sx={{ maxWidth: 360, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{a.detalle || '—'}</TableCell>
                <TableCell>
                  {fmtFechaEmision(a.fecha_documento) || '—'}
                  {a.detectada_en && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Detectado: {new Date(a.detectada_en).toLocaleDateString('es-PE')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={ETIQUETA_ESTADO[a.estado] || a.estado} color={a.estado === 'ACTIVA' ? 'error' : a.estado === 'EN_REVISION' ? 'warning' : 'default'} />
                  {a.nota && <Typography variant="caption" display="block" color="text.secondary">{a.nota}</Typography>}
                </TableCell>
                <TableCell align="center">
                  {(a.estado === 'ACTIVA' || a.estado === 'EN_REVISION') && (
                    <>
                      <Button size="small" onClick={() => { setAccion({ alerta: a, estado: 'RESUELTA_MANUAL' }); setNotaAccion(''); }}>Resolver</Button>
                      {a.estado === 'ACTIVA' && (
                        <Button size="small" onClick={() => { setAccion({ alerta: a, estado: 'EN_REVISION' }); setNotaAccion(''); }}>En revisión</Button>
                      )}
                    </>
                  )}
                  {a.estado === 'RESUELTA_MANUAL' && (
                    <Button size="small" onClick={() => { setAccion({ alerta: a, estado: 'ACTIVA' }); setNotaAccion(''); }}>Reabrir</Button>
                  )}
                  <Tooltip title="Datos para consultar en SUNAT (copiar/pegar)">
                    <IconButton size="small" onClick={() => verDatosSunat(a)}><AssignmentIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {a.origen === 'COMPROBANTE' && (a.estado === 'ACTIVA' || a.estado === 'EN_REVISION') && (
                    <Tooltip title="Verificar en SUNAT (API) y resolver si ya está aceptado">
                      <span>
                        <IconButton size="small" color="primary" disabled={verificando === a.id} onClick={() => verificarSunat(a)}>
                          {verificando === a.id ? <CircularProgress size={16} /> : <FactCheckIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {(a.origen === 'COMPROBANTE' || a.origen === 'GUIA') && (a.estado === 'ACTIVA' || a.estado === 'EN_REVISION') && (
                    <Tooltip title="Reenviar a SUNAT desde aquí (sin entrar al dominio de la empresa)">
                      <span>
                        <IconButton size="small" color="secondary" disabled={reenviando === a.id} onClick={() => reenviar(a)}>
                          {reenviando === a.id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {(a.origen === 'COMPROBANTE' || a.origen === 'GUIA') && (a.estado === 'ACTIVA' || a.estado === 'EN_REVISION') && (
                    <Tooltip title="Actualizar el estado real del documento (Aceptado o Rechazado) en la base de esa empresa">
                      <IconButton size="small" onClick={() => { setAccionEstadoReal({ alerta: a }); setEstadoRealElegido('ENVIADO'); setNotaEstadoReal(''); }}>
                        <RuleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Historial"><IconButton size="small" onClick={() => verHistorial(a)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                  {resultadoVerificacion?.alertaId === a.id && (
                    <Alert severity={resultadoVerificacion.resuelto ? 'success' : resultadoVerificacion.ok === false ? 'warning' : 'info'}
                      sx={{ mt: 0.5, py: 0, textAlign: 'left' }} onClose={() => setResultadoVerificacion(null)}>
                      {resultadoVerificacion.mensaje || resultadoVerificacion.error}
                    </Alert>
                  )}
                  {estadoEnVivo?.alertaId === a.id && (
                    <Alert severity={estadoEnVivo.estadoCpe && ['ENVIADO', 'ACEPTADO'].includes(estadoEnVivo.estadoCpe) ? 'success' : estadoEnVivo.terminado ? 'warning' : 'info'}
                      icon={estadoEnVivo.terminado ? undefined : <CircularProgress size={14} />}
                      sx={{ mt: 0.5, py: 0, textAlign: 'left' }} onClose={() => setEstadoEnVivo(null)}>
                      {estadoEnVivo.estadoCpe ? `Estado: ${estadoEnVivo.estadoCpe}${estadoEnVivo.estadoJob ? ` (job: ${estadoEnVivo.estadoJob})` : ''} — ` : ''}
                      {estadoEnVivo.mensaje}
                    </Alert>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!accion} onClose={() => setAccion(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {accion?.estado === 'RESUELTA_MANUAL' ? 'Marcar como resuelto' : accion?.estado === 'EN_REVISION' ? 'Marcar en revisión' : 'Reabrir alerta'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {accion?.alerta?.empresa} · {accion?.alerta?.tipo_doc} {accion?.alerta?.serie}-{accion?.alerta?.numero}
          </Typography>
          <TextField label="Nota (obligatoria)" value={notaAccion} onChange={(e) => setNotaAccion(e.target.value)} fullWidth multiline minRows={2} helperText="Explica qué se hizo o por qué sigue pendiente. Mínimo 5 caracteres." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarAccion} disabled={notaAccion.trim().length < 5}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!accionEstadoReal} onClose={() => setAccionEstadoReal(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Actualizar estado real del documento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {accionEstadoReal?.alerta?.empresa} · {accionEstadoReal?.alerta?.tipo_doc} {accionEstadoReal?.alerta?.serie}-{accionEstadoReal?.alerta?.numero}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esto actualiza el documento real en la base de esa empresa (no solo esta alerta). Úsalo cuando ya verificaste por tu cuenta (consulta manual o API) cuál es el estado real en SUNAT.
            No incluye "Anulado": anular requiere el flujo real de Anulación de esa empresa (comunica una baja a SUNAT), no un cambio de estado aquí.
          </Alert>
          <TextField select label="Estado real confirmado" value={estadoRealElegido} onChange={(e) => setEstadoRealElegido(e.target.value)} fullWidth sx={{ mb: 2 }}>
            <MenuItem value="ENVIADO">Aceptado en SUNAT</MenuItem>
            <MenuItem value="RECHAZADO">Rechazado por SUNAT (sin solución automática)</MenuItem>
          </TextField>
          <TextField label="Nota (obligatoria)" value={notaEstadoReal} onChange={(e) => setNotaEstadoReal(e.target.value)} fullWidth multiline minRows={2} helperText="Explica cómo confirmaste este estado. Mínimo 5 caracteres." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccionEstadoReal(null)}>Cancelar</Button>
          <Button variant="contained" onClick={actualizarEstadoReal} disabled={notaEstadoReal.trim().length < 5}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!historial} onClose={() => setHistorial(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Historial · {historial?.alerta?.empresa} {historial?.alerta?.serie}-{historial?.alerta?.numero}</DialogTitle>
        <DialogContent>
          {historial?.filas?.length ? historial.filas.map((f, i) => (
            <Box key={i} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" fontWeight={600}>{ETIQUETA_ESTADO[f.estado_anterior] || f.estado_anterior || '—'} → {ETIQUETA_ESTADO[f.estado_nuevo] || f.estado_nuevo}</Typography>
              <Typography variant="caption" color="text.secondary">{new Date(f.fecha).toLocaleString('es-PE')} · {f.usuario || 'sistema'}</Typography>
              {f.nota && <Typography variant="body2">{f.nota}</Typography>}
            </Box>
          )) : <Typography variant="body2">Sin cambios registrados.</Typography>}
        </DialogContent>
        <DialogActions><Button onClick={() => setHistorial(null)}>Cerrar</Button></DialogActions>
      </Dialog>

      <Dialog open={!!datosSunat} onClose={() => setDatosSunat(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Datos para consultar en SUNAT · {datosSunat?.alerta?.empresa} {datosSunat?.alerta?.serie}-{datosSunat?.alerta?.numero}
        </DialogTitle>
        <DialogContent>
          {datosSunat?.cargando && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>}
          {datosSunat?.error && <Alert severity="error">{datosSunat.error}</Alert>}
          {datosSunat?.datos && (() => {
            const d = datosSunat.datos;
            const campos = [
              ['Número de RUC del emisor', d.ruc],
              ['Tipo de comprobante', d.tipoComprobanteLabel],
              ['Serie', d.serie],
              ['Número', d.numero],
              ['Tipo/N° doc. del receptor', d.numDocReceptor ? `${d.tipoDocReceptorLabel} ${d.numDocReceptor}` : 'Sin documento'],
              ['Fecha de emisión', d.fechaEmision],
              ...(d.importeTotal != null ? [['Importe total', d.importeTotal]] : []),
            ];
            return (
              <>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Copia cada dato y pégalo en la{' '}
                  <a href="https://ww1.sunat.gob.pe/ol-ti-itconsultaunificadalibre/consultaUnificadaLibre/consulta" target="_blank" rel="noreferrer">
                    consulta individual de comprobantes de SUNAT
                  </a>.
                </Typography>
                <Table size="small">
                  <TableBody>
                    {campos.map(([label, valor]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ border: 0, pl: 0, color: 'text.secondary', whiteSpace: 'nowrap' }}>{label}</TableCell>
                        <TableCell sx={{ border: 0, fontWeight: 600, fontFamily: 'monospace' }}>{valor}</TableCell>
                        <TableCell sx={{ border: 0, pr: 0 }} align="right">
                          <Tooltip title="Copiar">
                            <IconButton size="small" onClick={() => copiar(valor)}><ContentCopyIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDatosSunat(null)}>Cerrar</Button>
          {datosSunat?.datos && <Button variant="contained" onClick={() => copiarTodo(datosSunat.datos)}>Copiar todo</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ControlComprobantes;
